/**
 * PasswordLoginForm - Password login form
 */
import React, { useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { useAuth } from '../../hooks/useAuth';
import { useAuthForm } from '../../hooks/useAuthForm';
import { AUTH_MODES } from '../../types';
import { AuthHeader, AuthInput, AuthButton } from '../ui';

function PasswordLoginForm() {
  const { identifier, loading, goBack, switchMode, setMode } = useAuthContext();
  const { loginWithPassword, sendOtp } = useAuth();
  const { password, error, handlePasswordChange } = useAuthForm();
  
  const [showPassword, setShowPassword] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) return;
    
    await loginWithPassword();
  };
  
  const handleLoginWithOtp = async () => {
    const success = await sendOtp();
    if (success) {
      setMode(AUTH_MODES.OTP_VERIFY);
    }
  };
  
  const EyeIcon = showPassword ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
  
  return (
    <div className="relative p-6 md:p-8 bg-white">
      <AuthHeader
        title="Enter Password"
        subtitle={`Logging in as ${identifier}`}
        showBack
        onBack={goBack}
      />
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthInput
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          placeholder="Enter your password"
          error={error}
          disabled={loading}
          autoFocus
          rightIcon={EyeIcon}
          onRightIconClick={() => setShowPassword(!showPassword)}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
        />
        
        <AuthButton
          type="submit"
          loading={loading}
          disabled={!password}
        >
          Login
        </AuthButton>
      </form>
      
      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">or</span>
        </div>
      </div>
      
      {/* Login with OTP */}
      <AuthButton
        type="button"
        variant="secondary"
        onClick={handleLoginWithOtp}
        loading={loading}
      >
        Login with OTP
      </AuthButton>
      
      {/* Forgot Password */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => switchMode(AUTH_MODES.FORGOT_PASSWORD)}
          className="text-sm text-gray-500 hover:text-[#004171] transition-colors"
        >
          Forgot Password?
        </button>
      </div>
    </div>
  );
}

export default PasswordLoginForm;
